import { CSSProperties } from "react";
import {
  AgentEffectCommands,
  AgentEffectDescription,
  AgentIcon,
  FactionType,
  Meaple,
  ResourceType,
  getEffectArg,
  sellSpiceAmounts,
} from "~/business";
import { FontSize, Spacing } from "~/jsx/theme";
import { Proposition, expectNever, parseEnumOrError } from "~/utils";
import { XAcquireTechCard } from "./XAcquireTechCard";
import { XAgentIcon } from "./XAgentIcon";
import { XCardFactionName } from "./XCardFactionName";
import { XFactionChange } from "./XFactionChange";
import { XFactionDiamond } from "./XFactionDiamond";
import { XMeaple } from "./XMeaple";
import { XOpponentSolaris } from "./XOpponentSolaris";
import { XResource } from "./XResource";
import { XResourceWithAmount } from "./XResourceWithAmount";
import { XTroop } from "./XTroop";
import { XTypography } from "./XTypography";
import {
  XArrowSvg,
  XCardIconSvg,
  XCombatPointSvg,
  XDiscardSvg,
  XDreadnought,
  XFactionVictoryPoint,
  XFoldspaceSvg,
  XFreighterSvg,
  XIntrigueCardSvg,
  XMeapleSvg,
  XSignetSvg,
  XSolarisSvg,
  XStealIntrigueSvg,
  XTechCardSvg,
  XTechNegociator,
  XTrashCardSvg,
  XTrashMeSvg,
  XVictoryPointSvg,
  XWaterSvg,
} from "./svg";

const { solaris, spice, water, cause } = AgentEffectCommands;

export type XAgentEffectContext =
  | "card"
  | "intrigueCard"
  | "actionSlot"
  | "battleCard"
  | "choice";

export const XAgentEffect = ({
  effectDescription,
  width,
  mentatOpt,
  context,
  layout = "horizontal",
  color,
  style,
}: {
  effectDescription: AgentEffectDescription;
  width: number;
  mentatOpt?: Meaple;
  context: XAgentEffectContext;
  layout?: "vertical" | "horizontal";
  color?: string;
  style?: CSSProperties;
}) => {
  const iconStyle: CSSProperties = {
    verticalAlign: "middle",
    display: "inline-block",
  };
  const iconWidth = width * 0.75;
  const iconProps = { width: iconWidth, style: iconStyle };
  const xTroop = <XTroop {...iconProps} />;
  const xDreadnought = <XDreadnought {...iconProps} />;

  return (
    <div style={{ color: color ?? "white", ...style }}>
      {Proposition.map(
        effectDescription,
        (command) => {
          const { methodName } = command;
          const isActionSlot = context === "actionSlot";
          const meapleSize = isActionSlot ? 32 : width;
          switch (methodName) {
            case "payResource": {
              const { type, amount } = getEffectArg<typeof methodName>(command);
              let newDescription;
              if (type === ResourceType.Solaris) {
                newDescription = solaris(amount);
              } else if (type === ResourceType.Spice) {
                newDescription = spice(amount);
              } else if (type === ResourceType.Water) {
                newDescription = water(amount);
              } else {
                throw new Error("not implemented");
              }
              return (
                <XAgentEffect
                  effectDescription={newDescription}
                  width={width}
                  color={color}
                  context={context}
                />
              );
            }
            case "influencePoints":
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <XResourceWithAmount
                  amount={amount}
                  type="influence"
                  width={width}
                />
              );
            case "buyWorker":
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <XTypography>+&nbsp;</XTypography>
                    <XMeapleSvg width={meapleSize} />
                  </div>
                  <XTypography>
                    (once <br />
                    by game)
                  </XTypography>
                </div>
              );

            case "water":
            case "spice":
            case "solaris": {
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <XResourceWithAmount
                  amount={amount}
                  type={parseEnumOrError(ResourceType, command.methodName)}
                  width={width}
                />
              );
            }
            case "garnison": {
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <XResourceWithAmount
                  amount={amount}
                  type={"troop"}
                  width={context === "intrigueCard" ? width * 0.5 : width}
                />
              );
            }
            case "draw":
              return <XCardIconSvg width={width} />;
            case "drawIntrigue":
              return <XIntrigueCardSvg width={width} />;
            case "combatPoint": {
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <XResourceWithAmount
                  amount={amount}
                  type="combatPoint"
                  width={width}
                />
              );
            }
            case "takeSiege":
              return (
                <XTypography>
                  <XResourceWithAmount
                    type="influence"
                    amount={2}
                    width={width}
                    style={{ display: "inline-block", verticalAlign: "middle" }}
                  />
                  /turn
                </XTypography>
              );
            case "trashCard": {
              const { onlyInPlay = false } =
                getEffectArg<typeof methodName>(command);
              return (
                <>
                  <XTrashCardSvg
                    width={width}
                    style={{
                      display: "block",
                      textAlign: "center",
                      margin: "auto",
                    }}
                  />
                  {onlyInPlay && (
                    <XTypography
                      typographyStyle={{
                        textAlign: "center",
                        display: "inline-block",
                      }}
                    >
                      only a played card
                    </XTypography>
                  )}
                </>
              );
            }
            case "trashMe":
            case "trashMeOptional":
              return <XTrashMeSvg width={width} />;
            case "buySpaceWarp":
              return <XFoldspaceSvg width={width} />;
            case "signet":
              return <XSignetSvg width={width} />;
            case "stealIntrigue":
              return <XStealIntrigueSvg width={width * 2} />;
            case "mentat":
            case "takeMentatFromAnywhere":
              return isActionSlot ? (
                <XMeaple width={meapleSize} meaple={mentatOpt} />
              ) : (
                <XMeapleSvg width={meapleSize} color="black" />
              );
            case "fremenBond":
              return (
                <XTypography>
                  <XCardFactionName faction={FactionType.Fremen} /> bond
                </XTypography>
              );
            case "hasAlliance": {
              const faction = getEffectArg<typeof methodName>(command);
              return (
                <XFactionVictoryPoint
                  faction={faction}
                  width={width}
                  style={{ padding: Spacing.px4 }}
                  noAnimation={true}
                />
              );
            }
            case "hasAnyAlliance":
              return <XTypography>Alliance on any faction</XTypography>;
            case "hasInfluenceLevel2":
              const faction = getEffectArg<typeof methodName>(command);
              return (
                <XTypography style={{ fontSize: FontSize.px16 }}>
                  <XFactionDiamond
                    width={width}
                    faction={faction}
                    style={{ display: "inline-block", verticalAlign: "middle" }}
                  />{" "}
                  &gt;= 2
                </XTypography>
              );
            case "hasPlayedBeneGesseritCard":
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                    alignItems: "center",
                  }}
                >
                  <XTypography>has played</XTypography>
                  <XCardFactionName faction={FactionType.BeneGesserit} />
                  <XTypography>card</XTypography>
                </div>
              );
            case "emperorUp":
              return (
                <XFactionChange
                  faction={FactionType.Emperor}
                  change={1}
                  width={width}
                />
              );
            case "beneGesseritUp":
              return (
                <XFactionChange
                  faction={FactionType.BeneGesserit}
                  change={1}
                  width={width}
                />
              );
            case "guildUp":
              return (
                <XFactionChange
                  faction={FactionType.Guild}
                  change={1}
                  width={width}
                />
              );
            case "fremenUp":
              return (
                <XFactionChange
                  faction={FactionType.Fremen}
                  change={1}
                  width={width}
                />
              );
            case "victoryPoint": {
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <>
                  {[...new Array(amount)].map((_, index) => (
                    <XVictoryPointSvg key={index} width={width} />
                  ))}
                </>
              );
            }
            case "chooseFactionChange": {
              const change = getEffectArg<typeof methodName>(command);
              return <XFactionChange change={change} width={width} />;
            }
            case "drawBenegesseritInDiscsard":
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <XTypography>Draw</XTypography>
                  <XCardFactionName faction={FactionType.BeneGesserit} />
                  <XTypography>in discard pile</XTypography>
                </div>
              );
            case "canRetreat": {
              const retreatAmount = getEffectArg<typeof methodName>(command);
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {retreatAmount <= 16 ? (
                    <XTypography>
                      You can retreat up to {retreatAmount} {xTroop}
                    </XTypography>
                  ) : (
                    <XTypography>
                      You can retreat as many {xTroop} as wanted
                    </XTypography>
                  )}
                </div>
              );
            }
            case "shouldRetreat": {
              const retreatAmount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Retreat {retreatAmount} {xTroop}
                </XTypography>
              );
            }
            case "opponentsDiscard": {
              const cardCount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>Opponents discards {cardCount} cards</XTypography>
              );
            }
            case "opponentsLooseGarnisonTroop": {
              const troopCount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Opponents loses {troopCount} {xTroop} in garnison
                </XTypography>
              );
            }
            case "opponentsLooseCombatTroop": {
              const troopCount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Opponents loses {troopCount} {xTroop} in combat
                </XTypography>
              );
            }
            case "canDeployToConflict": {
              const { amount, type } = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  {amount >= 20 ? (
                    <>
                      Deploy to conflict any number of{" "}
                      {type === "dreadnought" ? xDreadnought : xTroop}
                    </>
                  ) : (
                    <>
                      Deploy to conflict up to {amount}{" "}
                      {type === "dreadnought" ? xDreadnought : xTroop}
                    </>
                  )}
                </XTypography>
              );
            }
            case "sellSpice":
              return (
                <div>
                  {sellSpiceAmounts.map(
                    ({ spice: spiceAmount, solaris: solarisAmount }) => {
                      const effect = cause(spice(spiceAmount)).effect(
                        solaris(solarisAmount)
                      );
                      return (
                        <XAgentEffect
                          key={spiceAmount}
                          effectDescription={effect}
                          width={20}
                          color={color}
                          context={context}
                        />
                      );
                    }
                  )}
                </div>
              );
            case "canPassTurn":
              return <XTypography>You may pass your turn</XTypography>;
            case "hasSiege":
              return (
                <XTypography>
                  If you bought the siege to the High Council
                </XTypography>
              );
            case "hasNCardTSMF": {
              return (
                <XTypography
                  style={{ fontSize: FontSize.px12, marginBottom: 6 }}
                >
                  If you have at least 2 "The Spice Must Flow" cards
                </XTypography>
              );
            }
            case "hasMoreTSMFThanOpponents": {
              return (
                <XTypography style={{ fontSize: FontSize.px12 }}>
                  If you have more "The Spice Must Flow" cards than each
                  opponent
                </XTypography>
              );
            }
            case "hasFactionsLevel":
              const {
                factionCount = 4,
                maxLevel,
                minLevel,
              } = getEffectArg<typeof methodName>(command);
              const level = maxLevel === undefined ? minLevel : maxLevel;
              const isMore = minLevel !== undefined;
              return (
                <XTypography style={{ fontSize: FontSize.px12 }}>
                  Having {level} influence or {isMore ? "more" : "less"} on{" "}
                  {factionCount === 4 ? "all" : factionCount} factions
                </XTypography>
              );

            case "shuffleDiscardInDeck":
              return (
                <XTypography>
                  Shuffle your discard pile into your deck
                </XTypography>
              );
            case "recallAgent":
              return <XTypography>Recall one of your Agents</XTypography>;
            case "replaceOpponentConflictTroop":
              return (
                <XTypography>
                  Replace an ennemy {xTroop} in the conflict by one of your own
                </XTypography>
              );
            case "ignoreAgentIcons":
              return (
                <XTypography>
                  The next card you play may ignore the{" "}
                  <span
                    style={{
                      padding: 4,
                      borderRadius: 4,
                    }}
                  >
                    {Object.values(AgentIcon).map((agentIcon) => (
                      <XAgentIcon
                        key={agentIcon}
                        agentIcon={agentIcon}
                        width={12}
                      />
                    ))}
                  </span>{" "}
                  requirements
                </XTypography>
              );
            case "chooseLooseConflictTroops": {
              const troopCount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Remove {troopCount} of your {xTroop} from the conflict
                </XTypography>
              );
            }
            case "ignoreOccupiedAgentSlots":
              return (
                <XTypography>
                  Enemy Agents don't block your next Agent at board spaces this
                  turn
                </XTypography>
              );
            case "influenceByPlayedFactionCard": {
              const { amount, faction } =
                getEffectArg<typeof methodName>(command);
              return (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <XResourceWithAmount
                    amount={amount}
                    width={width}
                    type="influence"
                  />
                  <XTypography>&nbsp;/ played&nbsp;</XTypography>
                  <XCardFactionName faction={faction} />
                  <XTypography>&nbsp;card</XTypography>
                </div>
              );
            }
            case "doubleBaseSpice":
              return (
                <XTypography>Double base spice harvest (not bonus)</XTypography>
              );
            case "orChoice": {
              const effects = getEffectArg<typeof methodName>(command);
              return (
                <XMultipleEffects
                  effects={effects}
                  layout={layout}
                  width={width}
                  separator={effects.length >= 3 ? "/" : "or"}
                  color={color}
                  context={context}
                />
              );
            }
            case "gainControl": {
              const actionSlot = getEffectArg<typeof methodName>(command);
              return (
                <XTypography
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: FontSize.px10,
                  }}
                >
                  Control {actionSlot.id}
                </XTypography>
              );
            }
            case "multipleFactionChange": {
              const { factionUpdate, factionCount } =
                getEffectArg<typeof methodName>(command);
              return (
                <XTypography
                  typographyStyle={{
                    fontSize:
                      context === "battleCard" ? FontSize.px10 : FontSize.px14,
                    display: context === "battleCard" ? "flex" : "block",
                    alignItems: "center",
                  }}
                >
                  <XFactionChange
                    change={factionUpdate}
                    width={width}
                    style={{ display: "inline-block", verticalAlign: "middle" }}
                  />
                  <span>&nbsp;to {factionCount} factions</span>
                </XTypography>
              );
            }
            case "opponentDiscardOrLooseTroop": {
              return (
                <XTypography>
                  Opponents looses 1 {xTroop} in conflict or discards a card
                </XTypography>
              );
            }
            case "chooseGoToBattle": {
              return (
                <XTypography>
                  As you placed your agent on a battle location, you can deploy
                  some of your garnison units
                </XTypography>
              );
            }
            case "chooseFactionUpWhereBehind": {
              return (
                <XTypography>
                  You can increase your influence on a faction where you are not
                  the 1st player
                </XTypography>
              );
            }
            case "reserveShopCardWithDiscount": {
              const discount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  You can reserve a card from the shop, which you can buy at the
                  end of turn with a discount of{" "}
                  <XResourceWithAmount
                    style={{ verticalAlign: "middle" }}
                    type="influence"
                    amount={discount}
                    width={32}
                  />
                </XTypography>
              );
            }
            case "playAnywhere": {
              return (
                <XTypography>
                  You can play your agent on any slot, even occupied ones
                </XTypography>
              );
            }
            case "TSMFDiscount":
              const discount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  "The spice must flow" card cost{" "}
                  <XResourceWithAmount
                    style={{ verticalAlign: "middle" }}
                    type="influence"
                    amount={discount}
                    width={32}
                  />{" "}
                  less this turn
                </XTypography>
              );
            case "factionUp": {
              const faction = getEffectArg<typeof methodName>(command);
              return (
                <XFactionChange faction={faction} change={1} width={width} />
              );
            }
            case "drawOrTrashTopCard": {
              return (
                <XTypography>
                  Look at the top card of your deck, draw it or trash it.
                </XTypography>
              );
            }
            case "canBuyOnDeck": {
              return (
                <XTypography>
                  This turn, you can put the cards you buy on top of your deck
                </XTypography>
              );
            }
            case "buyOnDeck": {
              return (
                <XTypography>
                  You can put this card on top of your deck
                </XTypography>
              );
            }
            case "freeCard": {
              const { cardMaxCost, onTop } =
                getEffectArg<typeof methodName>(command);
              return (
                <XTypography
                  style={{ display: "inline-block", fontSize: FontSize.px12 }}
                >
                  {onTop ? "Put" : "Acquire"} a card with cost{" "}
                  <XResourceWithAmount
                    type="influence"
                    amount={cardMaxCost}
                    width={width * 0.75}
                    style={{ verticalAlign: "middle" }}
                  />{" "}
                  or less {onTop && "on top of your deck"}
                </XTypography>
              );
            }
            case "landsraadPopularity": {
              return (
                <XTypography>
                  Sending a{" "}
                  <XMeapleSvg
                    width={width}
                    style={{ verticalAlign: "middle" }}
                  />{" "}
                  to a{" "}
                  <span style={{ verticalAlign: "middle" }}>
                    <XAgentIcon
                      agentIcon={AgentIcon.Landsraad}
                      width={width * 0.75}
                    />
                  </span>{" "}
                  slot costs you{" "}
                  <XResourceWithAmount
                    amount={1}
                    type={ResourceType.Solaris}
                    width={width}
                    style={{ display: "inline-block", verticalAlign: "middle" }}
                  />{" "}
                  less
                </XTypography>
              );
            }
            case "eyesEverywhere": {
              return (
                <XTypography>
                  Enemy{" "}
                  <XMeapleSvg
                    width={width}
                    style={{ verticalAlign: "middle" }}
                  />{" "}
                  don't block your{" "}
                  <XMeapleSvg
                    width={width}
                    style={{ verticalAlign: "middle" }}
                  />{" "}
                  at{" "}
                  <span style={{ verticalAlign: "middle" }}>
                    <XAgentIcon
                      agentIcon={AgentIcon.Landsraad}
                      width={width * 0.75}
                    />
                  </span>{" "}
                  or{" "}
                  <span style={{ verticalAlign: "middle" }}>
                    <XAgentIcon
                      agentIcon={AgentIcon.City}
                      width={width * 0.75}
                    />
                  </span>{" "}
                  slots
                </XTypography>
              );
            }
            case "spiceAdict": {
              return (
                <XTypography>
                  When you harvest{" "}
                  <XResource
                    type={ResourceType.Spice}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                  />
                  : gain 1 less{" "}
                  <XResource
                    type={ResourceType.Spice}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                  />{" "}
                  and{" "}
                  <XAgentEffect
                    effectDescription={AgentEffectCommands.draw}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                    context={context}
                  />
                </XTypography>
              );
            }
            case "prescience": {
              return (
                <XTypography>
                  You may look at the top card of your deck at any time
                </XTypography>
              );
            }
            case "siegeConnections": {
              return (
                <XTypography>
                  When you take a High Counsil seat:{" "}
                  <XAgentEffect
                    effectDescription={AgentEffectCommands.chooseFactionChange(
                      1
                    )}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                    context={context}
                  />
                </XTypography>
              );
            }
            case "ruthlessNegotiator": {
              return (
                <XTypography>
                  When you pay{" "}
                  <XResource
                    type={ResourceType.Solaris}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                  />{" "}
                  for the cost of a slot:{" "}
                  <XAgentEffect
                    effectDescription={AgentEffectCommands.draw}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                    context={context}
                  />
                </XTypography>
              );
            }
            case "masterstroke": {
              return (
                <XTypography>
                  Once per game,{" "}
                  <XAgentEffect
                    effectDescription={AgentEffectCommands.multipleFactionChange(
                      {
                        factionCount: 2,
                        factionUpdate: 1,
                      }
                    )}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                    color={color}
                    context={context}
                  />
                </XTypography>
              );
            }
            case "blockActionSlot": {
              return (
                <XTypography>
                  Forbid opponents to play on a slot for this round
                </XTypography>
              );
            }
            case "discardCard": {
              return <XDiscardSvg width={width} />;
            }
            case "mayPlayAgain": {
              return <XTypography>You may play another turn</XTypography>;
            }
            case "freighter": {
              return <XFreighterSvg width={width} />;
            }
            case "freighterUp": {
              return <XTypography>Level up your freighter</XTypography>;
            }
            case "recallFreighter": {
              return <XTypography>Recall your freighter</XTypography>;
            }
            case "dreadnought": {
              return xDreadnought;
            }
            case "acquireTechTile": {
              const { discount, mayBuyWithSolaris } =
                getEffectArg<typeof methodName>(command);
              return (
                <div>
                  <XAcquireTechCard width={width * 1.6} discount={discount} />
                  {mayBuyWithSolaris && (
                    <XTypography>You can pay with solaris instead</XTypography>
                  )}
                </div>
              );
            }
            case "combatPointPerDreadnought": {
              const bonus = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Gain{" "}
                  <XResourceWithAmount
                    amount={bonus}
                    type="combatPoint"
                    variant="compact"
                    width={width * 0.75}
                    style={{ display: "inline-block", verticalAlign: "middle" }}
                  />{" "}
                  per dreadnought deployed to the conflict
                </XTypography>
              );
            }
            case "combatPointForReavealedCard": {
              const bonus = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Gain{" "}
                  <XResourceWithAmount
                    amount={bonus}
                    type="combatPoint"
                    width={width * 0.75}
                    style={{ verticalAlign: "middle" }}
                  />{" "}
                  per revealed card that provides{" "}
                  <XCombatPointSvg
                    width={width * 0.75}
                    style={{ verticalAlign: "middle" }}
                  />
                </XTypography>
              );
            }
            case "discardIntrigueCard": {
              return (
                <XTypography>
                  Put an intrigue card at the bottom of the intrigue draw pile
                </XTypography>
              );
            }
            case "techNegociator": {
              return <XTechNegociator width={width * 1.2} />;
            }
            case "hasAgentOnFactionSlot": {
              const faction = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>Has an agent on a {faction} slot</XTypography>
              );
            }
            case "heighlinerDiscount": {
              const discount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Heighliner slot cost
                  <XResourceWithAmount
                    amount={discount}
                    type={ResourceType.Spice}
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                  />{" "}
                  less
                </XTypography>
              );
            }
            case "hasMinInfluencePoints": {
              const minimum = getEffectArg<typeof methodName>(command);
              return (
                <XTypography
                  typographyStyle={{
                    textAlign: "center",
                    display: "inline-block",
                  }}
                >
                  Having at least{" "}
                  <XResourceWithAmount
                    amount={minimum}
                    type="influence"
                    width={width}
                    style={{
                      margin: "auto",
                      textAlign: "center",
                      display: "block",
                    }}
                  />
                </XTypography>
              );
            }
            case "spyStallites": {
              return (
                <XTypography>
                  <XVictoryPointSvg
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                  />{" "}
                  for each faction where your influence level is 1 or less
                </XTypography>
              );
            }
            case "recycleIntrigueCards": {
              return (
                <XTypography>Discard any number of intrigue cards</XTypography>
              );
            }
            case "drawIntrigueCardsToCheckpoint": {
              return <XTypography>Draw that many intrigue cards</XTypography>;
            }
            case "winTiebreakers": {
              return <XTypography>Win tiebreakers</XTypography>;
            }
            case "hasDeployedDreadnaught": {
              return (
                <XTypography>
                  Has deployed a dreadnought and do not take control of a slot
                </XTypography>
              );
            }
            case "shippingTrackTroop": {
              return (
                <XTypography>
                  Whenever you recruit {xTroop} from the freighter track,
                  recruit an additional {xTroop}. You may deploy them to the
                  conflict
                </XTypography>
              );
            }
            case "dreadnoughtControlSlot": {
              return (
                <XTypography>
                  As you won the conflict, choose a slot to control with your
                  dreadnought
                </XTypography>
              );
            }

            case "opponentsSolaris": {
              const { opponentsAmount, personalAmount } =
                getEffectArg<typeof methodName>(command);
              return (
                <XOpponentSolaris
                  personalAmount={personalAmount}
                  opponentAmount={opponentsAmount}
                  width={width}
                />
              );
            }
            case "hasNTechTile": {
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  bought {amount}{" "}
                  <XTechCardSvg
                    width={width}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                  />
                </XTypography>
              );
            }
            case "factionUpOnSlotWithAgent": {
              return (
                <XTypography>
                  <XAgentEffect
                    context={context}
                    width={width * 0.75}
                    effectDescription={AgentEffectCommands.chooseFactionChange(
                      1
                    )}
                    style={{ verticalAlign: "middle", display: "inline-block" }}
                  />{" "}
                  on faction slots where you have an agent
                </XTypography>
              );
            }
            case "hasResource": {
              const { amount, type } = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Has{" "}
                  <XResourceWithAmount
                    amount={amount}
                    type={type}
                    width={width}
                    variant="compact"
                  />
                </XTypography>
              );
            }
            case "looseTroop": {
              return <XTypography>Loose {xTroop}</XTypography>;
            }
            case "opponentsRetreatDreadnought": {
              return (
                <XTypography>
                  Opponents retreat 1 {xDreadnought} from the conflict
                </XTypography>
              );
            }
            case "canDeployToConflictUnit": {
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  Deploy to conflict up to {amount} units ({xTroop} or
                  {xDreadnought})
                </XTypography>
              );
            }
            case "grandConspiracy": {
              return (
                <XTypography typographyStyle={{ fontSize: FontSize.px12 }}>
                  3 of 4 conditions,{" "}
                  <XVictoryPointSvg width={width * 0.3} style={iconStyle} />
                  <ul style={{ paddingLeft: 10 }}>
                    <li>2{xDreadnought}</li>
                    <li>1 "The spice must flow" card</li>
                    <li>level 4+ on 2 factions</li>
                    <li>siege to the High Council</li>
                  </ul>
                  <br />
                  4 conditions, +
                  <XVictoryPointSvg width={width * 0.3} style={iconStyle} />.
                </XTypography>
              );
            }
            case "hasDeployedNUnits": {
              const amount = getEffectArg<typeof methodName>(command);
              return (
                <XTypography typographyStyle={{ fontSize: FontSize.px14 }}>
                  Has deployed {amount} units ({xTroop} or {xDreadnought}) to
                  the conflict this turn
                </XTypography>
              );
            }
            case "onWin": {
              const effect = getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  On win:{" "}
                  <XAgentEffect
                    context={context}
                    width={width}
                    effectDescription={effect}
                    style={iconStyle}
                  />
                </XTypography>
              );
            }
            case "isWithEnnemyAgent": {
              return (
                <XTypography>
                  Is played on a slot with an ennemy{" "}
                  <XMeapleSvg width={iconWidth} style={iconStyle} />
                </XTypography>
              );
            }
            case "ennemyRetreatForEachTroopDeployed": {
              return (
                <XTypography>
                  For each troop deployed this turn, force an opponent to
                  retreat a unit
                </XTypography>
              );
            }

            case "dreadnoughtStrength": {
              return (
                <XTypography>
                  Your {xDreadnought} are worth{" "}
                  <XResourceWithAmount
                    {...iconProps}
                    amount={4}
                    type="combatPoint"
                  />{" "}
                  instead of{" "}
                  <XResourceWithAmount
                    {...iconProps}
                    amount={3}
                    type="combatPoint"
                  />
                </XTypography>
              );
            }
            case "solarisBlessing": {
              return (
                <XTypography>
                  You start with no <XWaterSvg {...iconProps} />. When you gain{" "}
                  <XSolarisSvg {...iconProps} /> on your turn, increase the
                  amount by 1
                </XTypography>
              );
            }
            case "intrigueScry": {
              const { drawCount, lookCount } =
                getEffectArg<typeof methodName>(command);
              return (
                <XTypography>
                  At the start of the game, look at the top {lookCount}{" "}
                  <XIntrigueCardSvg {...iconProps} /> and keep {drawCount}
                </XTypography>
              );
            }
            case "trashOnRevealWhenPlayingNonFaction": {
              return (
                <XTypography>
                  When revealing your hand, if you have 2 agents or more on
                  <XAgentIcon {...iconProps} agentIcon={AgentIcon.SpiceTrade} />
                  ,
                  <XAgentIcon {...iconProps} agentIcon={AgentIcon.City} /> or
                  <XAgentIcon {...iconProps} agentIcon={AgentIcon.Landsraad} />
                  slots, you may <XTrashCardSvg {...iconProps} /> 1 played card
                  (on slot or revealed)
                </XTypography>
              );
            }
            case "planification": {
              return (
                <XTypography>
                  At the start of the round, set a card aside.
                  <br />
                  On your 2nd turn, reveal the card.
                  <br />
                  If you play it:
                  <br />
                  if it has only 1 agent icon, gain{" "}
                  <XResourceWithAmount
                    {...iconProps}
                    amount={1}
                    type={ResourceType.Spice}
                  />
                  , otherwise, gain{" "}
                  <XResourceWithAmount
                    {...iconProps}
                    amount={1}
                    type={ResourceType.Solaris}
                  />
                </XTypography>
              );
            }
            case "setTurnToPlayer":
            case "endPlayerTurn":
            case "checkEndBuy":
            case "checkEndReveal":
            case "endRound":
            case "toNextFightingPlayer":
            case "resolveWinnerDreadnought":
            case "choosePaymentForTechTile":
            case "ennemyRetreatNUnit":
              return (
                <XTypography>
                  type: {methodName}. Warning: you should not see this!
                </XTypography>
              );
            default:
              expectNever(methodName);
              throw new Error("expect never");
          }
        },
        ({ type, propositions }) => {
          return (
            <XMultipleEffects
              effects={propositions}
              layout={layout}
              width={width}
              context={context}
              separator={type === "or" ? "or" : undefined}
            />
          );
        },
        ({ cause, effect }) => {
          const sizeFactor = context === "actionSlot" ? 0.8 : 1;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: Spacing.px4,
                flexDirection: layout === "vertical" ? "column" : "row",
              }}
            >
              <XAgentEffect
                effectDescription={cause}
                width={width * sizeFactor}
                color={color}
                context={context}
              />
              <div
                style={{
                  paddingLeft: layout === "horizontal" ? Spacing.px4 : 0,
                }}
              >
                <XArrowSvg
                  size={width * (layout === "horizontal" ? 0.4 : 0.3)}
                  layout={layout}
                />
              </div>
              <XAgentEffect
                effectDescription={effect}
                width={width * sizeFactor}
                color={color}
                context={context}
              />
            </div>
          );
        }
      )}
    </div>
  );
};

const XMultipleEffects = ({
  effects,
  separator,
  layout,
  width,
  color,
  context,
}: {
  effects: AgentEffectDescription[];
  separator?: string;
  layout: "vertical" | "horizontal";
  width: number;
  color?: string;
  context: XAgentEffectContext;
}) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: Spacing.px8,
        flexDirection: layout === "vertical" ? "column" : "row",
      }}
    >
      {effects.flatMap((effect, index) => {
        const effectNode = (
          <span
            key={index}
            style={{ display: "flex", gap: Spacing.px8, alignItems: "center" }}
          >
            <XAgentEffect
              effectDescription={effect}
              width={width * (context === "battleCard" ? 0.7 : 1)}
              color={color}
              context={context}
            />
          </span>
        );
        return index > 0 && separator
          ? [
              <XTypography
                key={`or_${index}`}
                style={
                  context === "battleCard" ? { fontSize: width * 0.4 } : {}
                }
              >
                {separator.toUpperCase()}
              </XTypography>,
              effectNode,
            ]
          : [effectNode];
      })}
    </div>
  );
};
